import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Chip, Autocomplete, TextField } from '@mui/material';

export default function FieldsSelectorChips({
  allFields,
  selectedFields,
  setSelectedFields,
  allLabel = 'Todos los campos',
}) {
  const allOption = useMemo(() => ({
    label: allLabel,
    id: 'all_fields'
  }), [allLabel]);

  const options = useMemo(() => {
    const fieldsAsObjects = allFields.map((field) => ({
      label: field,
      id: field,
    }));
    return [allOption, ...fieldsAsObjects];
  }, [allFields, allOption]);

  const handleChange = (event, newValue) => {
    const hasAll = newValue.some((item) => item.id === 'all_fields');

    if (hasAll) {
      if (selectedFields.length === allFields.length) {
        setSelectedFields([]);
      } else {
        setSelectedFields([...allFields]);
      }
    } else {
      setSelectedFields(newValue.map((item) => item.id));
    }
  };

  const isAllSelected =
    selectedFields.length === allFields.length && allFields.length > 0;

  const currentValue = useMemo(() => {
    if (isAllSelected) {
      return [allOption];
    }
    return options.filter((opt) => selectedFields.includes(opt.id));
  }, [isAllSelected, selectedFields, options, allOption]);

  // Array de colores para los chips
  const chipColors = ['primary', 'secondary', 'success', 'error', 'info', 'warning'];

  const renderTags = (value, getTagProps) => {
    if (isAllSelected) {
      return (
        <Chip
          label={allLabel}
          {...getTagProps({ index: 0 })}
          key="all_fields"
          color="primary"
          variant="filled"
        />
      );
    }
    return value.map((option, index) => (
      <Chip
        key={option.id}
        label={option.label}
        {...getTagProps({ index })}
        color={chipColors[index % chipColors.length]}
        variant="filled"
        sx={{
          '& .MuiChip-deleteIcon': {
            color: 'white',
            '&:hover': {
              color: 'rgba(255, 255, 255, 0.7)',
            },
          },
        }}
      />
    ));
  };

  const getOptionStyle = (selected) => ({
    backgroundColor: selected ? 'rgba(25, 118, 210, 0.15)' : 'transparent',
    fontWeight: selected ? 600 : 400,
    padding: '8px 16px',
    borderLeft: selected ? '4px solid #1976d2' : '4px solid transparent',
    '&:hover': {
      backgroundColor: selected
        ? 'rgba(25, 118, 210, 0.25)'
        : 'rgba(0, 0, 0, 0.04)',
    },
  });

  const renderOption = (props, option, { selected }) => {
    const optionIndex = options.findIndex(opt => opt.id === option.id);
    const color = chipColors[optionIndex % chipColors.length];
    const colorMap = {
      primary: '#1976d2',
      secondary: '#9c27b0',
      success: '#2e7d32',
      error: '#d32f2f',
      info: '#0288d1',
      warning: '#ed6c02'
    };

    return (
      <li
        {...props}
        style={{
          ...props.style,
          ...getOptionStyle(selected),
          backgroundColor: selected ? `${colorMap[color]}15` : 'transparent',
          borderLeft: selected ? `4px solid ${colorMap[color]}` : '4px solid transparent',
        }}
      >
        <span style={{
          color: selected ? colorMap[color] : 'inherit',
          fontWeight: selected ? 600 : 400
        }}>
          {option.label}
        </span>
      </li>
    );
  };

  return (
    <Autocomplete
      multiple
      options={options}
      value={currentValue}
      onChange={handleChange}
      getOptionLabel={(option) => option.label}
      renderTags={renderTags}
      renderOption={renderOption}
      filterSelectedOptions
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          size="small"
          label="Campos a exportar"
          placeholder="Seleccione uno o varios campos"
        />
      )}
      sx={{ width: '100%' }}
    />
  );
}

FieldsSelectorChips.propTypes = {
  allFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedFields: PropTypes.func.isRequired,
  allLabel: PropTypes.string,
};